import * as THREE from 'three';
import type { HeroState } from '@moba/shared';

const FADE_IN = 0.12;
const FADE_OUT = 0.18;

let swirlTexture: THREE.CanvasTexture | null = null;
let wispTexture: THREE.CanvasTexture | null = null;

function makeSwirlTexture(size = 256): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const center = size / 2;
  const maxRadius = size * 0.42;

  ctx.lineCap = 'round';
  ctx.shadowColor = 'rgba(168, 76, 255, 0.9)';
  ctx.shadowBlur = size * 0.08;

  for (let arm = 0; arm < 3; arm++) {
    const offset = (arm / 3) * Math.PI * 2;
    for (let pass = 0; pass < 2; pass++) {
      ctx.beginPath();
      const steps = 100;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const angle = offset + t * Math.PI * 2 * 3.2;
        const radius = maxRadius * (0.16 + 0.84 * t);
        const x = center + Math.cos(angle) * radius;
        const y = center + Math.sin(angle) * radius;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = pass === 0 ? 'rgba(112, 34, 214, 0.55)' : 'rgba(230, 196, 255, 0.92)';
      ctx.lineWidth = size * (pass === 0 ? 0.034 : 0.013);
      ctx.stroke();
    }
  }

  const glow = ctx.createRadialGradient(center, center, 0, center, center, maxRadius * 0.4);
  glow.addColorStop(0, 'rgba(255, 255, 255, 0.85)');
  glow.addColorStop(0.35, 'rgba(205, 146, 255, 0.5)');
  glow.addColorStop(1, 'rgba(120, 40, 220, 0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(center, center, maxRadius * 0.4, 0, Math.PI * 2);
  ctx.fill();

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function makeWispTexture(size = 64): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const center = size / 2;
  const gradient = ctx.createRadialGradient(center, center, 0, center, center, center);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  gradient.addColorStop(0.3, 'rgba(214, 166, 255, 0.9)');
  gradient.addColorStop(1, 'rgba(120, 40, 220, 0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/**
 * Remolino violeta sobre la cabeza del héroe mientras está aturdido
 * (`HeroState.hitstun > 0`): dos espirales planas girando en sentidos opuestos
 * y cuatro motas orbitando para dar volumen al vórtice.
 */
export class StunSwirl {
  readonly group = new THREE.Group();
  private readonly discs: THREE.Mesh[] = [];
  private readonly discMaterials: THREE.MeshBasicMaterial[] = [];
  private readonly discOpacity: number[] = [];
  private readonly wisps: THREE.Sprite[] = [];
  private readonly wispMaterials: THREE.SpriteMaterial[] = [];
  private readonly radius: number;
  private presence = 0;
  private time = 0;

  constructor(heroRadius: number, height: number) {
    swirlTexture ??= makeSwirlTexture();
    wispTexture ??= makeWispTexture();

    this.radius = Math.max(heroRadius * 0.9, height * 0.24);
    this.group.position.y = height + 0.16;
    this.group.visible = false;

    const geometry = new THREE.PlaneGeometry(1, 1);
    geometry.rotateX(-Math.PI / 2);
    const specs = [
      { scale: this.radius * 2.6, y: 0, opacity: 0.95 },
      { scale: this.radius * 1.85, y: 0.13, opacity: 0.7 },
    ];
    for (const spec of specs) {
      const material = new THREE.MeshBasicMaterial({
        map: swirlTexture,
        color: 0xd7a6ff,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.y = spec.y;
      mesh.scale.setScalar(spec.scale);
      mesh.renderOrder = 24;
      this.group.add(mesh);
      this.discs.push(mesh);
      this.discMaterials.push(material);
      this.discOpacity.push(spec.opacity);
    }

    for (let i = 0; i < 4; i++) {
      const material = new THREE.SpriteMaterial({
        map: wispTexture,
        color: i % 2 === 0 ? 0xcf8cff : 0x9b4dff,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const sprite = new THREE.Sprite(material);
      sprite.scale.setScalar(this.radius * 0.45);
      sprite.renderOrder = 25;
      this.group.add(sprite);
      this.wisps.push(sprite);
      this.wispMaterials.push(material);
    }
  }

  update(dt: number, hero: HeroState): void {
    const active = hero.alive && hero.hitstun > 0;
    const rate = active ? 1 / FADE_IN : -1 / FADE_OUT;
    this.presence = THREE.MathUtils.clamp(this.presence + rate * dt, 0, 1);
    this.group.visible = this.presence > 0;
    if (!this.group.visible) return;

    this.time += dt;
    const p = this.presence;
    const pop = active ? 1 + Math.sin(p * Math.PI) * 0.16 : 1;
    this.group.scale.setScalar(Math.max(0.001, p * pop));

    const pulse = 0.82 + Math.sin(this.time * 9) * 0.18;
    this.discs.forEach((mesh, i) => {
      mesh.rotation.y += dt * (i === 0 ? 2.6 : -3.4);
      this.discMaterials[i].opacity = this.discOpacity[i] * p * pulse;
    });

    const orbit = this.time * 4.2;
    this.wisps.forEach((sprite, i) => {
      const angle = orbit + (i / this.wisps.length) * Math.PI * 2;
      const radius = this.radius * (0.72 + 0.12 * Math.sin(this.time * 3 + i));
      sprite.position.set(
        Math.cos(angle) * radius,
        this.radius * (0.3 + 0.16 * Math.sin(this.time * 5 + i * 1.7)),
        Math.sin(angle) * radius,
      );
      this.wispMaterials[i].opacity = p * (0.55 + 0.35 * Math.sin(this.time * 7 + i * 2));
      sprite.scale.setScalar(this.radius * (0.34 + 0.1 * Math.sin(this.time * 6 + i)));
    });
  }
}
